/**
 * The menu.
 *
 * Not the *main* menu — that is the screen the game loads into, and it is a
 * different thing with a different job. This is the overlay you can summon at
 * any point: from the cockpit, from a berth, from the yard. It holds the
 * settings and the way back to the main menu, and nothing that belongs to
 * playing.
 *
 * ── It does not pause ───────────────────────────────────────────────────────
 *
 * Deliberately, and for the same reason the lesson cards do not: other players
 * are in the room and the ship is still flying. A pause that stopped nothing
 * would be a lie about what is happening behind it, and one that stopped
 * *everything* is not available in a game with other people in it.
 *
 * So it is an overlay over a live game, and it says so by leaving the world
 * visible behind it rather than blacking it out.
 *
 * ── One settings surface ────────────────────────────────────────────────────
 *
 * Music and the feature introductions used to sit on the main menu, which meant
 * they could only be changed before starting — and that a player who wanted the
 * music down mid-flight had to abandon the flight to do it. They live here now,
 * and the main menu opens this rather than keeping its own copy. Two panels
 * offering one setting is two panels that can disagree about it.
 */

import { useEffect, useState } from 'react';

import { HUD_BACKING, HUD_DIM, HUD_EDGE, HUD_ORANGE, HUD_TEXT } from './FlightHud';

export interface GameMenuProps {
  onClose: () => void;

  /**
   * Back to the main menu, or absent when that is where we already are.
   *
   * The main menu opens this for its settings, and offering "return to the main
   * menu" from the main menu would be a button that does nothing.
   */
  onMainMenu?: () => void;

  /** How loud the soundtrack is, 0..1, and the steps offered. */
  music: number;
  musicSteps: readonly number[];
  onSetMusic: (level: number) => void;

  /**
   * Whether the loud half of a first-time lesson is wanted.
   *
   * Only the loud half — the one-line reminders in the ship's console keep going
   * either way, because those are the interface rather than a tutorial.
   */
  tips: boolean;
  onToggleTips: (on: boolean) => void;
}

export function GameMenu({
  onClose,
  onMainMenu,
  music,
  musicSteps,
  onSetMusic,
  tips,
  onToggleTips,
}: GameMenuProps) {
  /*
   * Whether the document is fullscreen, watched rather than remembered.
   *
   * The browser can leave fullscreen without being asked — Escape does it, and
   * so does a phone rotating or an alert arriving — so a flag set when the
   * button was pressed would go stale within a minute. The change event is the
   * only thing that knows.
   */
  const [fullscreen, setFullscreen] = useState(isFullscreen);

  useEffect(() => {
    const onChange = () => setFullscreen(isFullscreen());

    // Both spellings. Safari fires the prefixed one and nothing else, and a
    // browser that fires both simply sets the same value twice.
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Dimmed rather than blacked out: the game is still running behind this
        // and hiding that would be the same lie as pretending to pause.
        background: 'rgba(6,3,2,0.72)',
        pointerEvents: 'auto',
        zIndex: 70,
      }}
      // A click on the backdrop is a click outside the panel, which everywhere
      // else in the game means "I am done here".
      onClick={onClose}
    >
      <div
        // Stops a click inside the panel from reaching the backdrop and closing
        // it — the single most irritating bug this pattern has.
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(420px, 84vw)',
          maxHeight: '80vh',
          overflowY: 'auto',
          background: HUD_BACKING,
          border: `1px solid ${HUD_ORANGE}`,
          boxShadow: '0 0 44px rgba(0,0,0,0.7)',
          color: HUD_TEXT,
          padding: '16px 18px 14px',
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: HUD_DIM,
            marginBottom: 12,
          }}
        >
          menu
        </div>

        {/* ── Settings ──────────────────────────────────────────────────── */}
        <Heading>settings</Heading>

        <Row label="music">
          <span style={{ display: 'flex', gap: 3 }}>
            {musicSteps.map((step, index) => {
              // Filled up to and including the current level. The zero step is
              // the "off" end and is never drawn filled — an empty row is what
              // off looks like.
              const on = step > 0 && music >= step - 0.001;

              return (
                <button
                  key={step}
                  type="button"
                  onClick={() => onSetMusic(step)}
                  title={step === 0 ? 'Music off' : `Music at ${Math.round(step * 100)}%`}
                  style={{
                    width: index === 0 ? 15 : 12,
                    height: 18,
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: on ? HUD_ORANGE : HUD_DIM,
                    font: 'inherit',
                    fontSize: 13,
                    lineHeight: '18px',
                    cursor: 'pointer',
                    opacity: step === 0 && music === 0 ? 1 : step === 0 ? 0.55 : 1,
                  }}
                >
                  {step === 0 ? '×' : on ? '▮' : '▯'}
                </button>
              );
            })}
          </span>
        </Row>

        {/* Fullscreen.
            Worth a row of its own on a phone, where the address bar and the
            system chrome are a third of the screen — and harmless on a desktop,
            where it is the difference between a game and a tab. */}
        <Row label="fullscreen">
          <button
            type="button"
            disabled={!FULLSCREEN}
            onClick={toggleFullscreen}
            title={
              FULLSCREEN
                ? 'Fills the screen. Leave it the same way, or with escape.'
                : 'This device will not put a web page fullscreen.'
            }
            style={{
              ...QUIET,
              color: fullscreen ? HUD_TEXT : HUD_DIM,
              borderColor: fullscreen ? HUD_EDGE : 'rgba(255,255,255,0.14)',
              cursor: FULLSCREEN ? 'pointer' : 'not-allowed',
              opacity: FULLSCREEN ? 1 : 0.5,
            }}
          >
            {/* "home screen" rather than "n/a" where the bars are already
                gone by another route — the switch cannot work, and saying only
                that would understate what the player has. */}
            {!FULLSCREEN ? (STANDALONE ? 'home screen' : 'n/a') : fullscreen ? 'on' : 'off'}
          </button>
        </Row>

        {/*
          Said rather than hidden.

          The row used to disappear where the API was missing, on the argument
          that a control which cannot work is worse than no control. That was
          wrong in the one case it was written for: a player goes looking for the
          setting, does not find it, and concludes the game is broken — which is
          worse than a greyed switch and a sentence naming who is refusing.

          "iPhone" rather than "iOS", which an earlier version of this said and
          which is wrong: an iPad has had fullscreen since iOS 12 and takes the
          switch above like anything else.
        */}
        {!FULLSCREEN && (
          <div style={{ fontSize: 10.5, color: HUD_DIM, lineHeight: 1.45, marginTop: -2 }}>
            {STANDALONE
              ? `Running from the Home Screen, so the browser's bars are already
                 gone. iPhone has no fullscreen switch to offer beyond that.`
              : `iPhone gives no fullscreen to a web page, in any browser — they
                 all run on Safari underneath. Share, then Add to Home Screen,
                 opens the game without the bars.`}
          </div>
        )}

        <Row label="feature intros">
          <button
            type="button"
            onClick={() => onToggleTips(!tips)}
            title={
              tips
                ? 'On. The ship console keeps its one-line reminders either way.'
                : 'Off. The ship console still explains things in passing.'
            }
            style={{
              ...QUIET,
              color: tips ? HUD_TEXT : HUD_DIM,
              borderColor: tips ? HUD_EDGE : 'rgba(255,255,255,0.14)',
            }}
          >
            {tips ? 'on' : 'off'}
          </button>
        </Row>

        {/* The half of a lesson that has no switch, said once so nobody goes
            looking for the switch. */}
        <div style={{ fontSize: 10.5, color: HUD_DIM, lineHeight: 1.45, marginTop: 4 }}>
          The ship console keeps its one-line reminders whichever way that is set.
          Press a marked line there to read the full entry again.
        </div>

        {/* ── Leaving ───────────────────────────────────────────────────── */}
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <button type="button" onClick={onClose} style={ACTION}>
            resume
          </button>

          {onMainMenu && (
            <button type="button" onClick={onMainMenu} style={QUIET}>
              return to main menu
            </button>
          )}

          {/* An anchor rather than a button: it is a link, and it gets the
              middle-click, the context menu and the status bar for free. */}
          <a href="/games/" style={{ ...QUIET, textAlign: 'center', textDecoration: 'none' }}>
            back to games
          </a>
        </div>
      </div>
    </div>
  );
}

/*
 * ── Fullscreen, in two spellings and sometimes neither ──────────────────────
 *
 * The one place in this codebase where the folklore is worth writing down,
 * because almost everything said about it online is either out of date or about
 * the wrong device.
 *
 * ── iPad: yes, and has been since iOS 12 ────────────────────────────────────
 *
 * Element fullscreen has worked on iPad for years, through the prefixed
 * `webkitRequestFullscreen`. Safari 16.4 (March 2023) added the *unprefixed*
 * API on macOS and iPadOS, so both spellings have to be checked: an
 * `document.fullscreenEnabled` test on its own answers "no" on an iPad running
 * anything older, which can do it perfectly well.
 *
 * Apple's implementation shows an overlay button to leave fullscreen that the
 * page cannot suppress. That is by design and not worth fighting.
 *
 * ── iPhone: no, and not because of the browser ──────────────────────────────
 *
 * There is no element fullscreen on iPhone. It has existed behind a Safari
 * feature flag since 17.2 and has never shipped switched on — through iOS 26,
 * `caniuse` still records the API as iPad-only.
 *
 * And it holds for *every* browser on the platform, because they are all Safari
 * underneath. So "it isn't showing on iOS Chrome" is not a Chrome bug and
 * cannot be fixed by asking Chrome differently. What does work there is adding
 * the game to the Home Screen, which since iOS 17 opens a site as a web app
 * without the browser's bars — and since 16.4 any browser can offer it, Chrome
 * included, not only Safari.
 *
 * ── The Home Screen is a workaround, not the API ────────────────────────────
 *
 * Worth being exact about, because the two get conflated. Adding the game to the
 * Home Screen removes the *browser's* chrome — no address bar, no toolbar — and
 * that is most of what fullscreen was wanted for. It does not make
 * `requestFullscreen` work, and it does not take the status bar: the switch
 * still reads unavailable inside a Home Screen web app, and it is telling the
 * truth when it does. See `STANDALONE`.
 *
 * ── Mechanics ───────────────────────────────────────────────────────────────
 *
 * The prefixed calls return `undefined` rather than a promise, hence the
 * deliberate absence of a `.catch` on that path.
 */
interface PrefixedDocument extends Document {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => void;
}

interface PrefixedElement extends HTMLElement {
  webkitRequestFullscreen?: () => void;
}

/** Whether this browser will put a page fullscreen at all. Decided once. */
const FULLSCREEN: boolean = (() => {
  if (typeof document === 'undefined') return false;

  const prefixed = document as PrefixedDocument;
  return document.fullscreenEnabled === true || prefixed.webkitFullscreenEnabled === true;
})();

/**
 * Whether the game was opened from the Home Screen rather than in a browser.
 *
 * Which is not the same as being fullscreen, and the difference is the whole
 * reason this exists. A Home Screen web app has no address bar and no toolbar —
 * on an iPhone that is as close to fullscreen as the platform goes — but the
 * Fullscreen *API* is still missing, so the switch above still cannot work. What
 * changes is the advice underneath it: telling somebody to add the game to their
 * Home Screen when they are plainly already running it from there is the kind of
 * thing that makes a player distrust everything else the menu says.
 *
 * Both spellings, because iOS answers to either and neither is guaranteed:
 * `display-mode` is the standard media feature, and `navigator.standalone` is
 * WebKit's own, older, and the one that has always worked there.
 */
const STANDALONE: boolean = (() => {
  if (typeof window === 'undefined') return false;

  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  } catch {
    // No `matchMedia` is old enough that it is not a Home Screen web app.
  }

  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
})();

function isFullscreen(): boolean {
  if (typeof document === 'undefined') return false;

  const prefixed = document as PrefixedDocument;
  return Boolean(document.fullscreenElement ?? prefixed.webkitFullscreenElement);
}

function toggleFullscreen(): void {
  if (!FULLSCREEN) return;

  const prefixed = document as PrefixedDocument;
  const root = document.documentElement as PrefixedElement;

  /*
   * Rejections are ignored on purpose.
   *
   * A browser refuses a request that did not come from a gesture, and there is
   * nothing useful to say about that: the change event reports the truth either
   * way, so the button shows what actually happened rather than what was asked
   * for.
   */
  if (isFullscreen()) {
    if (document.exitFullscreen) void document.exitFullscreen().catch(() => undefined);
    else prefixed.webkitExitFullscreen?.();
    return;
  }

  if (root.requestFullscreen) void root.requestFullscreen().catch(() => undefined);
  else root.webkitRequestFullscreen?.();
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 9.5,
        letterSpacing: 1.6,
        textTransform: 'uppercase',
        color: HUD_ORANGE,
        borderBottom: `1px solid ${HUD_EDGE}`,
        paddingBottom: 4,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 8,
        fontSize: 12,
      }}
    >
      <span>{label}</span>
      {children}
    </div>
  );
}

const ACTION: React.CSSProperties = {
  background: 'rgba(255,138,61,0.16)',
  border: `1px solid ${HUD_ORANGE}`,
  color: HUD_ORANGE,
  font: 'inherit',
  fontSize: 13,
  padding: '8px 14px',
  cursor: 'pointer',
};

const QUIET: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_DIM,
  font: 'inherit',
  fontSize: 12,
  padding: '7px 12px',
  cursor: 'pointer',
  display: 'block',
};
